import {
  Button,
  CircularProgress,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@material-ui/core"
import AddCircleIcon from "@material-ui/icons/AddCircle"
import Delete from "@material-ui/icons/Delete"
import DescriptionIcon from "@material-ui/icons/Description"
import { useContext, useEffect, useState } from "react"
import { useMutation, useQueryClient } from "react-query"
import { Link } from "react-router-dom"
import { deleteSrt, getVideoSrt } from "~/actions/lessons"
import { addSrt, requestSrtUpload, uploadSrt } from "~/actions/upload"
import EditIcon from "~/assets/Icons/edit-icon.svg"
import Box from "~/components/Common/Box"
import CloudflareVideoUpload from "~/components/Common/CloudflareVideoUpload"
import DarkButton from "~/components/Common/DarkButton"
import FileUpload from "~/components/Common/FileUpload"
import HoverText from "~/components/Common/HoverText"
import PermissionsWrapper from "~/components/Common/PermissionsWrapper"
import PlayerWithControls from "~/components/Common/PlayerWithControls"
import { SRT_LANGUAGES } from "~/constants/languages"
import AppContext from "~/contexts/App"
import LearningCuesContext from "~/contexts/LearningCues"
import LessonContext from "~/contexts/Lesson"
import { VideoContainer } from "~/contexts/Video"
import AddLanguageToSrtDialog from "~/dialogs/AddLanguageToSrt"
import useLearningCueMediaFileName from "~/hooks/useLearningCueMediaFileName"
import useThumbnail from "~/hooks/useThumbnail"
import useStyles from "./styles"

const LessonContent = () => {
  const classes = useStyles()
  const theme = useTheme()
  const mdDown = useMediaQuery(theme.breakpoints.down("md"))
  const smDown = useMediaQuery(theme.breakpoints.down("sm"))

  const { videoUrl, lesson } = useContext(LessonContext)
  const { openSnackBar, openDialog } = useContext(AppContext)
  const { learningCuesQuery, cueCount } = useContext(LearningCuesContext)
  const lessonThumbnail = useThumbnail(lesson?.thumbnail, 1920)

  const queryClient = useQueryClient()

  const [loading, setLoading] = useState(false)

  const videoUploaded = videoUrl?.isSuccess

  const disabledLesson =
    (lesson.published || lesson.has_been_published) && lesson.archived === 0

  const requestSrtUploadMutation = useMutation(
    "requestSrtUpload",
    requestSrtUpload,
  )
  const deleteSrtMutation = useMutation(deleteSrt)
  const mediaKeyName = useLearningCueMediaFileName(videoUrl?.data?.key)

  const uploadSrtFile = async (
    srt,
    videoUploaded,
    captionId,
    srtLanguage,
    lesson,
  ) => {
    if (lesson.video_id || videoUploaded) {
      setLoading(true)
      const timestamp = +new Date()
      requestSrtUploadMutation
        .mutateAsync({
          key: `srt/${srtLanguage}/${timestamp}-${srt.name}`,
          type: "text/plain",
        })
        .then((res) => {
          uploadSrt({ ...res.data, file: srt })
            .then(() =>
              addSrt(
                {
                  key: res.data.fields.key,
                  caption_id: captionId,
                  srt_language: srtLanguage,
                },
                lesson?.video_id,
              ),
            )
            .then((addSrtRes) =>
              queryClient.setQueryData(["lesson", lesson.id], (oldData) => {
                oldData.captions = addSrtRes.data
                return oldData
              }),
            )
            .catch((err) =>
              openSnackBar({
                message: "There was an error uploading this srt file",
              }),
            )
            .finally(() => setLoading(false))
        })
    } else {
      openSnackBar({ message: "You must add a video before adding captions" })
    }
  }

  const onDrop = (acceptedFiles, videoUploaded, caption) => {
    let data = {
      acceptedFiles,
      videoUploaded,
      caption,
      lesson,
    }

    openDialog("addLanguageToSrt", data)
      .then((res) => {
        const { lesson, acceptedFiles, videoUploaded, srtLanguage, captionId } =
          res
        uploadSrtFile(
          acceptedFiles[0],
          videoUploaded,
          captionId,
          srtLanguage,
          lesson,
        )
      })
      .catch((err) => {
        console.error("err", err)
      })
  }

  const handleOpenSrt = (captionId) => {
    getVideoSrt(captionId)
      .then((res) => window.open(res))
      .catch(() =>
        openSnackBar({ message: "There was an error showing the srt file" }),
      )
  }

  useEffect(() => {
    if (videoUrl?.data?.data?.readyToStream === false) {
      setTimeout(() => {
        videoUrl.refetch(lesson?.video_id)
      }, [10000])
    }
    if (videoUrl?.data?.data?.duration) {
      queryClient.setQueryData(["lesson", lesson.id], (oldData) => {
        oldData.duration = Math.floor(videoUrl?.data?.data?.duration)
        oldData.video.duration = videoUrl?.data?.data?.duration
        return oldData
      })
    }
  }, [videoUrl?.data?.data])

  const renderPublishedBanner = () => {
    if (disabledLesson) {
      return (
        <Box className={classes.publishedBanner}>
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
          >
            {lesson?.published === 1 ? (
              <Typography className={classes.title} variant="h3">
                You are in a published lesson, this cannot be updated
              </Typography>
            ) : null}
            {lesson?.published === 0 && lesson?.has_been_published === 1 ? (
              <Typography className={classes.title} variant="h3">
                You are in a unpublished lesson, this cannot be updated
              </Typography>
            ) : null}
            <Typography variant="subtitle1" className={classes.subtitle}>
              To update this you must create a new version
            </Typography>
          </Box>
        </Box>
      )
    }
    return null
  }
  const renderCloudflareVideoUpload = () => {
    return (
      <Box
        paddingRight="28px"
        width={mdDown ? "100%" : "75%"}
        position="relative"
      >
        {videoUrl?.isLoading && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="100%"
            height="100%"
          >
            <CircularProgress color="primary" size={15} />
          </Box>
        )}
        {videoUrl?.data?.data?.readyToStream && (
          <Box>
            <VideoContainer lesson={lesson}>
              {({ ReactPlayerProps }) => (
                <>
                  <Typography variant="h6" className={classes.previewTitle}>
                    Video Preview
                  </Typography>
                  <PlayerWithControls
                    url={`https://customer-${
                      import.meta.env.VITE_CLOUDFLARE_CUSTOMER_ID
                    }.cloudflarestream.com/${
                      videoUrl?.data?.data.videoToken
                    }/manifest/video.m3u8`}
                    poster={lessonThumbnail}
                    ReactPlayerProps={ReactPlayerProps}
                    duration={lesson?.duration || 0}
                  />
                </>
              )}
            </VideoContainer>
            <Typography variant="h4" className={classes.mediaKey}>
              {videoUrl?.data.data.fileName}
            </Typography>
          </Box>
        )}
        {videoUrl?.data?.data?.readyToStream === false && (
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            width="100%"
            height="100%"
            position="absolute"
            top="0"
            lef="0"
            zIndex="100"
            right="0"
            bottom="0"
            marginRight="28px"
            style={{ background: "#D0D5DD" }}
          >
            <Typography style={{ fontWeight: 700 }}>
              Video not ready to stream, come back in a few minutes
            </Typography>
            <CircularProgress color="primary" size={15} className="mt-2" />
          </Box>
        )}

        {!disabledLesson && !videoUrl?.isLoading && (
          <CloudflareVideoUpload mediaKeyName={mediaKeyName} />
        )}
      </Box>
    )
  }

  const renderLessonQueues = () => {
    const cueText = (cueCount, mdDown) => {
      if (mdDown) {
        return "Add Learning Cue"
      } else if (cueCount) {
        return "Add another learning cue"
      } else {
        return "Add your first learning cue"
      }
    }
    return (
      <Box
        marginTop="28px"
        className={classes.lessonCues}
        flexShrink={0}
        width={mdDown && !smDown ? "10%" : smDown ? "50%" : "25%"}
      >
        {learningCuesQuery.isLoading ? (
          <CircularProgress color="primary" />
        ) : (
          <>
            <Typography className={classes.cueCountText} color="secondary">
              {cueCount ? cueCount : `0`}
            </Typography>
            <Typography
              color="secondary"
              variant="h5"
              className={classes.cuesText}
            >
              Lesson Cues
            </Typography>
            <DarkButton
              disabled={lesson.video_id === null || Boolean(disabledLesson)}
              style={disabledLesson ? { display: "none" } : null}
              component={Link}
              to={`/lessons/${lesson.id}/cues`}
              size="large"
              startIcon={<AddCircleIcon />}
            >
              {cueText(cueCount, mdDown)}
            </DarkButton>
          </>
        )}
      </Box>
    )
  }

  const renderSrtUpload = () => {
    const showDeleteSrt = lesson?.captions?.length > 1
    return (
      <Box className={classes.srtUploadContainer}>
        <Typography color="secondary" className={classes.inputLabel}>
          Captions
        </Typography>
        <Box className="flex flex-wrap flex-row">
          {lesson?.captions?.map((caption, i) => (
            <Box key={i} style={{ marginLeft: "10px" }}>
              <FileUpload
                title="Add .srt file here"
                DropZoneProps={{ accept: ".srt" }}
                onDrop={(acceptedFiles, videoUploaded) =>
                  onDrop(acceptedFiles, videoUploaded, caption)
                }
                showLoading={loading}
                type="srt"
                videoUploaded={videoUploaded}
              >
                {caption && (
                  <Box className={classes.srtContainer}>
                    <Box className={classes.editBox}>
                      <PermissionsWrapper permissionGroup="admin">
                        <img
                          src={EditIcon}
                          alt="Edit file"
                          style={{ width: 24 }}
                        />
                      </PermissionsWrapper>
                    </Box>
                    {showDeleteSrt && (
                      <PermissionsWrapper permissionGroup="admin">
                        <IconButton
                          className={classes.delete}
                          color="inherit"
                          style={{ zIndex: 100 }}
                          onClick={(e) => {
                            e.stopPropagation()
                            e.preventDefault()
                            openDialog("confirmationTypingDialog", {
                              title: "Delete Caption",
                              type: "caption",
                              confirmWord: "delete",
                              delete: deleteSrtMutation,
                              id: caption.id,
                              lessonId: lesson.id,
                            })
                          }}
                        >
                          <Delete style={{ color: "red" }} />
                        </IconButton>
                      </PermissionsWrapper>
                    )}

                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      flexDirection="column"
                      width={271}
                    >
                      <DescriptionIcon color="primary" />
                    </Box>
                  </Box>
                )}
              </FileUpload>
              <div className="flex justify-between w-full">
                <HoverText
                  data={caption?.srt_key?.split("-")?.[1]}
                  className={classes.mediaKey}
                  size={30}
                />

                <Typography
                  variant="subtitle1"
                  color="primary"
                  className={classes.mediaKey}
                >
                  {Object.keys(SRT_LANGUAGES).map((language, index) => {
                    if (caption?.srt_language === language) {
                      return SRT_LANGUAGES[language]
                    }
                  })}
                </Typography>
              </div>
              {caption ? (
                <Button
                  onClick={() => handleOpenSrt(caption.id)}
                  color="secondary"
                  style={{ marginTop: "16px" }}
                >
                  View srt file
                </Button>
              ) : null}
            </Box>
          ))}
          <Box
            style={{
              minWidth: "120px",
              marginLeft: "10px",
            }}
          >
            <FileUpload
              title="Add .srt file here"
              DropZoneProps={{ accept: ".srt" }}
              onDrop={onDrop}
              showLoading={loading}
              type="srt"
              videoUploaded={videoUploaded}
            />
          </Box>
        </Box>
        <AddLanguageToSrtDialog />
      </Box>
    )
  }

  return (
    <>
      {renderPublishedBanner()}

      <Box className={classes.contentContainer}>
        {renderCloudflareVideoUpload()}
        {renderLessonQueues()}
        {mdDown && !smDown && renderSrtUpload()}
      </Box>
      {(smDown || !mdDown) && <Box>{renderSrtUpload()}</Box>}
    </>
  )
}

export default LessonContent
